import { twJoin, twMerge } from 'tailwind-merge';
import {
  memo,
  KeyboardEventHandler,
  forwardRef,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { OmitInputDefaultClassname } from '../dataInput/BaseInput';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { dropdown, errorMessage } from '@/utils/animations/commom';
import ConditionalRenderer from '../ConditionalRenderer';
import { secondaryInput } from '@/styles/styles';
import useToggle from '@/data/hook/useToggle';
import IconButton from '../buttons/IconButton';
import { useDropdownKeyboard } from '@/data/hook/useDropdownKeyboard';
import { KeyboardKeys } from '@/utils/Keyboard';

export type SecondarySelectOption = {
  label: string;
  value: string | number;
};

type Props = Omit<OmitInputDefaultClassname, 'value' | 'onChange'> & {
  label?: string;
  className?: ClassNameComponent;
  error?: string;
  mask?: (value: string) => string;
  maxLength?: number;
  options: Array<SecondarySelectOption>;
  onChange: (option: string | number | null) => void;
  value?: string | number;
  disabled?: boolean;
  testId?: string;
};

const ChevronDown = motion.create(ChevronDownIcon);

const SecondarySelect = forwardRef<HTMLInputElement, Props>(
  (props: Props, ref) => {
    const {
      id,
      label,
      className,
      error,
      mask,
      autoComplete = 'off',
      options,
      onChange,
      value,
      disabled,
      testId,
      ...rest
    } = props;

    const { isOpen, close, open } = useToggle();
    const containerRef = useRef<HTMLDivElement>(null);

    const { focusedIndex, handleKeyDown, setFocusedIndexByMouse } =
      useDropdownKeyboard({
        isOpen,
        optionsLength: options.length,
        onOpen: open,
        onClose: close,
        onSelect: index => onChange(options[index].value),
      });

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          close();
        }
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === KeyboardKeys.Escape) {
          close();
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [isOpen, close]);

    const maskHandler: KeyboardEventHandler<HTMLInputElement> = e => {
      const input = e.currentTarget;
      if (mask) {
        input.value = mask(input.value);
      }
    };

    const option = useMemo(
      () => options.find(option => option.value === value),
      [options, value],
    );

    return (
      <div
        ref={containerRef}
        className={twMerge(
          secondaryInput,
          'flex gap-1 min-h-8 w-full relative',
          'transition-all duration-200 ease-in-out',
          error && !disabled && 'border-error focus-within:border-error',
          disabled && 'pointer-events-none disabled',
          className?.base,
        )}
      >
        <div className="flex items-center gap-1">
          <ConditionalRenderer condition={label}>
            <label
              htmlFor={id}
              className={twMerge(
                'font-500 whitespace-nowrap',
                className?.label,
              )}
            >
              {label}
            </label>
          </ConditionalRenderer>
          <AnimatePresence>
            {error && !disabled && (
              <motion.label
                className="text-error text-[0.875rem] overflow-hidden max-h-5"
                {...errorMessage}
              >
                *
              </motion.label>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-1 w-full overflow-hidden">
          <input
            id={id}
            ref={ref}
            {...rest}
            data-testid={testId}
            value={value ? option?.label : ''}
            aria-label={option?.label}
            autoComplete={autoComplete}
            onKeyUp={maskHandler}
            onKeyDown={handleKeyDown}
            onFocus={open}
            onClick={open}
            disabled={disabled}
            readOnly
            className={twMerge(
              'grow focus:outline-hidden select-text cursor-pointer flex w-full bg-transparent',
              className?.input,
            )}
          />

          <ConditionalRenderer condition={!disabled}>
            <ConditionalRenderer
              condition={value}
              fallback={
                <ChevronDown
                  animate={{
                    rotate: isOpen ? 180 : 0,
                  }}
                  className={twMerge(
                    'w-5 text-primary',
                    error && 'text-error',
                    className?.icon,
                  )}
                />
              }
            >
              <IconButton
                icon={<XMarkIcon />}
                onClick={() => onChange(null)}
                className={twJoin(
                  'w-5 text-error rounded-full',
                  'transition-all duration-200 ease-in-out',
                  'hover:text-error-content',
                )}
              />
            </ConditionalRenderer>
          </ConditionalRenderer>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.ul
              {...dropdown}
              className={twMerge(
                secondaryInput,
                'absolute z-50 top-10 left-0 flex flex-col w-full shadow-default p-2 gap-1',
                className?.ul,
              )}
            >
              {options.map((option, index) => (
                <li
                  className={twJoin(
                    'cursor-pointer flex items-center w-full p-1 rounded-md',
                    'transition-all duration-200 ease-in-out',
                    'hover:bg-primary-content',
                    option.value === value && 'bg-primary-content text-primary',
                    focusedIndex === index &&
                      'bg-primary-content text-primary ring-1 ring-primary',
                  )}
                  role="option"
                  aria-selected={option.value === value}
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                  onMouseEnter={() => setFocusedIndexByMouse(index)}
                >
                  {option.label}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

export default memo(SecondarySelect);
